import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  AlertTriangle,
  Droplets,
  Wind,
  CloudLightning,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function PrinceAlbertRepair() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof repair cost in Prince Albert?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Roof repairs in Prince Albert typically range from $300 for minor patching to $2,500+ for extensive damage involving decking or flashing. We offer free, fixed-price estimates.",
        },
      },
      {
        "@type": "Question",
        name: "Do you repair ice dam damage?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, ice dams are very common in Prince Albert. We can repair the damaged shingles and decking, and suggest ventilation improvements to prevent them from recurring.",
        },
      },
      {
        "@type": "Question",
        name: "How quickly can you fix a leaking roof?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We prioritize active leaks to prevent interior damage. We can often provide emergency tarping within 24 hours, followed by a permanent repair once weather permits.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Prince Albert",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Repair",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Prince Albert",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Prince Albert",
        item: "https://www.saskroofingcompany.com/areas/prince-albert",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Repair",
        item: "https://www.saskroofingcompany.com/areas/prince-albert/repair",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Repair Prince Albert | Sask Roofing Company"
        description="Expert roof repair in Prince Albert, SK. We fix leaks, ice dam damage, and missing shingles quickly and permanently. Get a free, honest repair estimate."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b963df3a-8576-4537-8ff4-9d7e9bcdc2c5.png&output=webp&q=60"
              alt="Prince Albert Roof Repair"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Repair Prince Albert
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Northern Saskatchewan winters are harsh on roofs. If you have a
                leak, missing shingles, or ice dam damage, our Prince Albert
                team provides fast, permanent repairs to protect your home.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get a Free Repair Estimate</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Don't Ignore Roofing Issues
                </h2>
                <p className="text-lg text-muted-foreground">
                  In Prince Albert, a small roof defect can quickly become a
                  major disaster during the spring thaw or a heavy summer rain.
                  Addressing repairs early saves you money and prevents mold and
                  structural rot.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">We expertly repair:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Droplets className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Ice Dam Leaks:</strong> Fixing damage caused
                        when melting snow backs up under your shingles.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wind className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Missing Shingles:</strong> Replacing sections
                        blown off by strong winds to restore your roof's
                        integrity.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Chimney & Vent Leaks:</strong> Resealing
                        deteriorated flashing that commonly causes localized
                        leaks.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CloudLightning className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Punctures & Tree Damage:</strong> Repairing
                        decking and shingles damaged by falling branches.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f1796618-613f-4a0c-8e31-90786ba9c4af.png&output=webp&q=60"
                  alt="Inspecting a roof in Prince Albert"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                The Sask Roofing Difference
              </h2>
              <p className="text-lg text-muted-foreground">
                We believe in honest assessments. If your roof can be saved with
                a repair, we won't push you to buy a whole new roof.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Accurate Diagnostics</h3>
                <p className="text-muted-foreground">
                  Water travels in strange ways. We trace leaks back to their
                  true source, ensuring we fix the actual problem, not just the
                  symptom.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Quality Materials</h3>
                <p className="text-muted-foreground">
                  We use premium sealants, high-grade flashing, and
                  climate-appropriate shingles to ensure our repairs last
                  through Prince Albert winters.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Transparent Pricing</h3>
                <p className="text-muted-foreground">
                  You receive a firm, written quote before any work begins. No
                  hidden fees or surprise upcharges on your final bill.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Prince Albert
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Prince Albert, standard residential roof
                  repairs—such as patching leaks, replacing blown-off shingles,
                  or resealing flashing—do not require a building permit.
                  However, if the repair involves fixing structural damage to
                  the roof trusses from heavy snow loads or replacing large
                  sections of rotted decking, a permit from the City of Prince
                  Albert may be required. We assess your roof's structural
                  integrity during our free inspection and manage all necessary
                  permitting and compliance, ensuring your repair meets all
                  local and provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Prince Albert
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Prince Albert experiences severe winter storms and heavy snow
                  loads that can cause localized roof damage and severe ice
                  damming. If your roof has been damaged by a storm or ice
                  buildup, dealing with insurance can be stressful. We
                  specialize in storm damage restoration and frequently work
                  with local insurance adjusters. If you suspect damage, call us
                  for a free inspection before filing a claim. We will document
                  the damage with detailed photos and provide a comprehensive
                  report for your adjuster, ensuring you get the full coverage
                  you are entitled to for a proper roof repair.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Prince Albert Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Prince Albert roof repairs, we use premium materials
                  designed to withstand the harsh northern climate. We source
                  high-grade sealants that remain flexible in sub-zero
                  temperatures and use architectural shingles that closely match
                  your existing roof's color and weathering. We also utilize
                  heavy-gauge metal flashing to permanently seal vulnerable
                  areas around chimneys and vents, which are prone to ice
                  damming leaks. All our repair work is backed by a robust
                  workmanship warranty, giving you peace of mind that the leak
                  is fixed for good.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      300+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Repairs in Prince Albert
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      24h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Emergency Tarping
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Premium
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Sealants Used
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f1796618-613f-4a0c-8e31-90786ba9c4af.png&output=webp&q=60"
                  alt="Prince Albert roof repair process"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Prince Albert Roof Repair Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We work quickly to secure your home without cutting corners on
                  quality.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Thorough Inspection
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We assess the damage inside and out to determine the
                        extent of the necessary repairs.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Fixed Quote</h4>
                      <p className="text-muted-foreground mt-1">
                        We provide a clear, written estimate for the repair
                        work.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Expert Execution
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our skilled technicians perform the repair, matching
                        materials as closely as possible and ensuring a
                        watertight seal.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Prince Albert roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Prince Albert:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/prince-albert"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Prince Albert roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/prince-albert/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Replacement in Prince Albert
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Prince Albert Roof Repair FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof repair cost in Prince Albert?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Roof repairs in Prince Albert typically range from $300 for
                  minor patching to $2,500+ for extensive damage involving
                  decking or flashing. We offer free, fixed-price estimates so
                  you know the exact cost.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do you repair ice dam damage?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Yes, ice dams are very common in Prince Albert. We can repair
                  the damaged shingles and decking, and suggest ventilation
                  improvements to prevent them from recurring next winter.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How quickly can you fix a leaking roof?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  We prioritize active leaks to prevent interior damage. We can
                  often provide emergency tarping within 24 hours, followed by a
                  permanent repair once weather permits.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Need a Roof Repair Fast?
            </h2>
            <p className="text-xl opacity-90">
              Contact us today for a free inspection and get your Prince Albert
              roof fixed by the experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Repair Quote</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
